/**
 * The reporting check (piece 31; decision 0933): our Profit and Loss and
 * Balance Sheet beside Zoho Books' for a pulled range, section by section,
 * each row carrying both figures and their difference, the rows that differ
 * first. A pure function the server and the suites share, so the order a
 * person reads is decided in one place.
 */
import { BOOKS_REPORT_KINDS, BOOKS_REPORT_SECTIONS_OF, type BooksReportKind, type BooksReportSection } from './booksSource';

export interface ReportCompareRow {
  report: BooksReportKind;
  section: BooksReportSection;
  /** Ours, in whole cents and the section's own sign; null where our statement named no such line. */
  ours: number | null;
  /** Zoho Books', the same way; null where its answer named no such section. */
  zoho: number | null;
  /** Ours less Zoho's, a missing figure read as zero. */
  difference: number;
  /** 🔒 The section's accounts, ours beside Zoho's, the differing ones first (T3344); empty on a result such as net profit. */
  accounts: ReportCompareAccount[];
  /** 🔒 Decision 10541: both figures stand and carry opposite signs, so the difference is a sign question and not an amount one; absent reads false. */
  signDiffers?: boolean;
}

/**
 * 🔒 Decision 10541 (T553): a section whose two figures are both above or below nothing on opposite sides is a sign
 * question. A missing figure, a zero and two figures on one side are not.
 */
export function compareSignDiffers(ours: number | null, zoho: number | null): boolean {
  return ours !== null && zoho !== null && ours !== 0 && zoho !== 0 && (ours > 0) !== (zoho > 0);
}

/** One account of a section on the reporting check (T3344): ours beside Zoho's, a side that names no such account blank. */
export interface ReportCompareAccount {
  /** Our account's number where our chart holds it, through our own line or block 1's reference; null where only Zoho names it or our line names no account. */
  accountNumber: string | null;
  name: string;
  /** Ours, in whole cents and the section's own sign; null where our statement drew no such account in this section. */
  ours: number | null;
  /** Zoho Books', the same way; null where its answer named no such account in this section. */
  zoho: number | null;
  /** Ours less Zoho's, a missing figure read as zero. */
  difference: number;
}

/** One account line of one side of a section, keyed so the two sides meet: our account's id where one is known, else a key the side alone holds. */
export interface ReportCompareAccountLine {
  key: string;
  accountNumber: string | null;
  name: string;
  amountCents: number;
}

export interface ReportCompareView {
  from: string;
  to: string;
  rows: ReportCompareRow[];
  /** What a person reads beside the figures: a section one side did not name, and what the opening entry left in 3100. */
  notices: string[];
  /** 🔒 Zoho Books' answer for each report as it came, for the screen's download (T3484); absent from an answer that carries none. */
  zohoAnswers?: Partial<Record<BooksReportKind, unknown>>;
}

/**
 * 🔒 Every section of both statements, ours beside Zoho's, the rows that
 * differ first by the size of the difference, and then the rest in the order
 * the statements print (decision 0933).
 */
export function compareRows(
  ours: Readonly<Record<BooksReportKind, Partial<Record<BooksReportSection, number>>>>,
  zoho: Readonly<Record<BooksReportKind, Partial<Record<BooksReportSection, number>>>>,
  accounts: Readonly<Partial<Record<BooksReportSection, readonly ReportCompareAccount[]>>> = {},
): ReportCompareRow[] {
  const rows: (ReportCompareRow & { order: number })[] = [];
  let order = 0;
  for (const report of BOOKS_REPORT_KINDS) {
    for (const section of BOOKS_REPORT_SECTIONS_OF[report]) {
      const mine = ours[report][section] ?? null;
      const theirs = zoho[report][section] ?? null;
      rows.push({ report, section, ours: mine, zoho: theirs, difference: (mine ?? 0) - (theirs ?? 0), accounts: [...(accounts[section] ?? [])], order: order++ });
    }
  }
  return rows
    .sort((a, b) => {
      const differs = Number(b.difference !== 0) - Number(a.difference !== 0);
      if (differs !== 0) return differs;
      if (a.difference !== 0) return Math.abs(b.difference) - Math.abs(a.difference) || a.order - b.order;
      return a.order - b.order;
    })
    .map(({ order: _order, ...row }) => row);
}

/**
 * 🔒 One section's accounts, ours beside Zoho's (T3344): lines sharing a key meet, several lines on one key sum, and a
 * side that names no line for a key reads blank, never zero. An account both sides carry at nothing is left out. The
 * accounts that differ come first by the size of the difference; the rest follow ours in print order, then Zoho's.
 */
export function compareAccounts(ours: readonly ReportCompareAccountLine[], zoho: readonly ReportCompareAccountLine[]): ReportCompareAccount[] {
  const met = new Map<string, ReportCompareAccount & { order: number }>();
  let order = 0;
  const add = (line: ReportCompareAccountLine, side: 'ours' | 'zoho') => {
    const at = met.get(line.key) ?? { accountNumber: line.accountNumber, name: line.name, ours: null, zoho: null, difference: 0, order: order++ };
    at[side] = (at[side] ?? 0) + line.amountCents;
    if (side === 'ours') {
      at.name = line.name;
      at.accountNumber = line.accountNumber ?? at.accountNumber;
    } else at.accountNumber ??= line.accountNumber;
    met.set(line.key, at);
  };
  for (const line of ours) add(line, 'ours');
  for (const line of zoho) add(line, 'zoho');
  return [...met.values()]
    .filter((at) => (at.ours ?? 0) !== 0 || (at.zoho ?? 0) !== 0)
    .map((at) => ({ ...at, difference: (at.ours ?? 0) - (at.zoho ?? 0) }))
    .sort((a, b) => {
      const differs = Number(b.difference !== 0) - Number(a.difference !== 0);
      if (differs !== 0) return differs;
      if (a.difference !== 0) return Math.abs(b.difference) - Math.abs(a.difference) || a.order - b.order;
      return a.order - b.order;
    })
    .map(({ order: _order, ...at }) => at);
}

/** The compare route, relative to the API root (piece 31). */
export const ZOHO_BOOKS_COMPARE_PATH = '/import/zoho-books/compare';

/** 🔒 Mark the reports checked (decision 1760): one audited acknowledgement of the comparison of the range last written. */
export const ZOHO_BOOKS_COMPARE_ACCEPT_PATH = '/import/zoho-books/compare/accept';

export function zohoBooksComparePath(from: string, to: string): string {
  return `${ZOHO_BOOKS_COMPARE_PATH}?${new URLSearchParams({ from, to }).toString()}`;
}
